import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Send,
  Sparkles,
  Users,
  Briefcase,
  Heart,
} from "lucide-react";
import { Link } from "react-router-dom";

import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { Reveal } from "./Reveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const PARTNERSHIP_TYPES = [
  "MWBE Procurement / Vendor",
  "Workforce Training (SYEP)",
  "Mentorship Program",
  "Community Sponsorship",
  "Food Service Partnership",
  "Press / Media",
  "Other",
];

const ORG_TYPES = [
  "Government Agency",
  "Nonprofit",
  "School / University",
  "Corporate",
  "Community Organization",
  "Foundation",
  "Individual",
  "Other",
];

const initialState = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  organization_type: "",
  title: "",
  partnership_type: "",
  website: "",
  message: "",
};

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "Name is required";
  if (!v.email.trim()) e.email = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "Enter a valid email";
  if (!v.partnership_type) e.partnership_type = "Choose a partnership type";
  if (!v.message.trim() || v.message.trim().length < 20)
    e.message = "Tell us a little more (at least 20 characters)";
  return e;
};

const PILLARS = [
  {
    icon: Briefcase,
    label: "MWBE Procurement",
    body: "We're a NYC MWBE-certified food vendor. Public-sector and corporate procurement teams welcome.",
  },
  {
    icon: Users,
    label: "Workforce + SYEP",
    body: "We host SYEP students, train new line cooks, and partner with workforce development orgs.",
  },
  {
    icon: Heart,
    label: "Community Sponsorship",
    body: "Block events, school events, mutual-aid meals — we say yes whenever we can.",
  },
  {
    icon: Sparkles,
    label: "Strategic Partners",
    body: "Press, brand collaborations, content, mentorship — let's talk.",
  },
];

const PartnershipPage = () => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [referenceId, setReferenceId] = useState("");

  const update = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setValues((s) => ({ ...s, [key]: val }));
    if (errors[key]) setErrors((s) => ({ ...s, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-field='${firstKey}']`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/partnership/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        const detail = data?.detail
          ? typeof data.detail === "string"
            ? data.detail
            : JSON.stringify(data.detail)
          : `HTTP ${res.status}`;
        setSubmitError(`We couldn't submit your inquiry: ${detail}. Please try again or call us directly.`);
      } else {
        setReferenceId(data.reference_id || "");
        setSubmitted(true);
      }
    } catch (err) {
      setSubmitError(err?.message || "Network error. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setValues(initialState);
    setErrors({});
    setSubmitted(false);
    setSubmitError("");
    setReferenceId("");
  };

  return (
    <div
      data-testid="partnership-page"
      className="bg-brand-cream text-brand-charcoal antialiased min-h-screen flex flex-col"
    >
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-brand-charcoal text-white py-20 sm:py-24 lg:py-28">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <Link
                to="/"
                data-testid="partner-back-home"
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold hover:text-white transition-colors"
              >
                ← Back home
              </Link>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-8 text-[12px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                Partner With Us
              </p>
            </Reveal>
            <Reveal delay={2}>
              <h1
                data-testid="partner-headline"
                className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
              >
                Procurement, workforce, community —
                <span className="text-brand-gold"> let&rsquo;s build something.</span>
              </h1>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-6 max-w-2xl text-[16px] sm:text-[17px] leading-relaxed text-white/80">
                Fresh Fast Foods, LLC is an MWBE-certified NYC food operator. We partner with
                public agencies, nonprofits, schools, and corporates across procurement,
                workforce training (SYEP), mentorship, and community sponsorship. Tell us a
                little about what you have in mind.
              </p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PILLARS.map((p, i) => (
                <Reveal key={p.label} delay={4 + i}>
                  <div
                    data-testid={`partner-pillar-${i}`}
                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-5 py-5 flex items-start gap-4"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gold text-brand-charcoal">
                      <p.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-gold">
                        {p.label}
                      </div>
                      <p className="mt-1.5 text-[14px] leading-snug text-white/80">{p.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <div className="rounded-3xl bg-white border border-black/5 shadow-[0_24px_60px_-30px_rgba(17,17,17,0.25)] p-6 sm:p-10">
                {submitted ? (
                  <div
                    data-testid="partner-success"
                    className="flex flex-col items-center text-center py-10"
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
                      <CheckCircle2 className="h-8 w-8" />
                    </span>
                    <h3 className="mt-6 font-display font-black text-2xl sm:text-3xl tracking-tight">
                      Thanks. Your inquiry is in.
                    </h3>
                    <p className="mt-3 max-w-md text-[15px] text-brand-charcoal/70 leading-relaxed">
                      Our partnerships team will reach out within two business days. Check your
                      email for a confirmation.
                    </p>
                    {referenceId ? (
                      <p
                        data-testid="partner-reference-id"
                        className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/50"
                      >
                        Reference: {referenceId}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      data-testid="partner-submit-another-btn"
                      onClick={reset}
                      className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={onSubmit}
                    noValidate
                    data-testid="partnership-form"
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                  >
                    <div className="md:col-span-2">
                      <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-brand-teal">
                        Partnership inquiry
                      </p>
                      <h2 className="mt-2 font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight">
                        Tell us how you&rsquo;d like to partner.
                      </h2>
                      <p className="mt-2 text-[14px] text-brand-charcoal/65 leading-relaxed">
                        Looking for catering instead?{" "}
                        <Link
                          to="/quote"
                          data-testid="partner-redirect-quote"
                          className="text-brand-teal font-semibold hover:underline"
                        >
                          Request a catering quote →
                        </Link>
                      </p>
                    </div>

                    <Field label="Your Name *" error={errors.name} name="name">
                      <Input
                        data-testid="partner-name"
                        value={values.name}
                        onChange={update("name")}
                        placeholder="Jane Doe"
                        className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>
                    <Field label="Title / Role" name="title">
                      <Input
                        data-testid="partner-title"
                        value={values.title}
                        onChange={update("title")}
                        placeholder="Procurement Director"
                        className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>
                    <Field label="Email *" error={errors.email} name="email">
                      <Input
                        data-testid="partner-email"
                        type="email"
                        value={values.email}
                        onChange={update("email")}
                        placeholder="you@organization.org"
                        className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>
                    <Field label="Phone" name="phone">
                      <Input
                        data-testid="partner-phone"
                        type="tel"
                        value={values.phone}
                        onChange={update("phone")}
                        placeholder="(212) 555-0123"
                        className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>

                    <Field label="Organization / Company" name="organization">
                      <Input
                        data-testid="partner-organization"
                        value={values.organization}
                        onChange={update("organization")}
                        placeholder="NYC DOE, Goldman Sachs, etc."
                        className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>
                    <Field label="Organization Type" name="organization_type">
                      <Select
                        value={values.organization_type}
                        onValueChange={update("organization_type")}
                      >
                        <SelectTrigger
                          data-testid="partner-organization-type"
                          className="h-12 rounded-xl bg-white border-black/10 focus:ring-2 focus:ring-brand-teal"
                        >
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ORG_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field
                      label="Partnership Type *"
                      error={errors.partnership_type}
                      name="partnership_type"
                      className="md:col-span-2"
                    >
                      <Select
                        value={values.partnership_type}
                        onValueChange={update("partnership_type")}
                      >
                        <SelectTrigger
                          data-testid="partner-partnership-type"
                          className="h-12 rounded-xl bg-white border-black/10 focus:ring-2 focus:ring-brand-teal"
                        >
                          <SelectValue placeholder="How would you like to partner?" />
                        </SelectTrigger>
                        <SelectContent>
                          {PARTNERSHIP_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Website" name="website" className="md:col-span-2">
                      <Input
                        data-testid="partner-website"
                        type="url"
                        value={values.website}
                        onChange={update("website")}
                        placeholder="https://yourorganization.org"
                        className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>

                    <Field
                      label="Tell us more *"
                      error={errors.message}
                      name="message"
                      className="md:col-span-2"
                    >
                      <Textarea
                        data-testid="partner-message"
                        value={values.message}
                        onChange={update("message")}
                        placeholder="Share the program, timeline, scope, and how you imagine us collaborating…"
                        rows={6}
                        className="rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                      />
                    </Field>

                    {submitError ? (
                      <div
                        data-testid="partner-submit-error"
                        role="alert"
                        className="md:col-span-2 inline-flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                      >
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span className="leading-snug">{submitError}</span>
                      </div>
                    ) : null}

                    <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="partner-submit-btn"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-7 py-4 text-[13px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_18px_40px_-18px_rgba(17,17,17,0.5)]"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            Send Partnership Inquiry
                            <Send className="h-4 w-4" />
                          </>
                        )}
                      </button>
                      <p className="text-[12px] text-brand-charcoal/60 leading-snug">
                        By submitting, you agree to be contacted by Fresh Fast Foods about your
                        partnership inquiry.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

const Field = ({ label, error, name, children, className = "" }) => (
  <div data-field={name} className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/70">
      {label}
    </Label>
    {children}
    {error && (
      <p
        className="text-[12px] text-red-600 font-medium"
        data-testid={`partner-error-${name}`}
        role="alert"
      >
        {error}
      </p>
    )}
  </div>
);

export default PartnershipPage;
